import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import type {SelectProps} from "@mui/material/Select";
import type {SxProps, Theme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import {getReservationStatusOptions} from './reservationStatusOptions';
import {
    getEventBackgroundColor,
    getEventColorBasedOnStatus
} from "crqlar-react-common/dist/utils/color.util";

interface Props {
    id?: string
    value?: ReservationStatus
    isExistingReservation: boolean
    disabled?: boolean
    fullWidth?: boolean
    ariaLabel?: string
    open?: boolean
    onOpen?: SelectProps['onOpen']
    onClose?: SelectProps['onClose']
    menuProps?: SelectProps['MenuProps']
    onChange: (status: ReservationStatus) => void
    sx?: SxProps<Theme>
}

const ReservationStatusSelect = ({
                                     id,
                                     value,
                                     isExistingReservation,
                                     disabled = false,
                                     fullWidth = false,
                                     ariaLabel,
                                     open,
                                     onOpen,
                                     onClose,
                                     menuProps,
                                     onChange,
                                     sx
                                 }: Props) => {
    const {t} = useTranslation()
    const statusColor = getEventColorBasedOnStatus(value)
    const statusBackgroundColor = getEventBackgroundColor(value)

    const statusSx: SxProps<Theme> = statusColor ? {
        backgroundColor: statusBackgroundColor,
        color: statusColor,
        border: `1px solid ${statusColor}`,
        '& .MuiSelect-icon': {
            color: statusColor,
        },
        '& .MuiOutlinedInput-notchedOutline': {
            border: 'none',
        },
        '&.Mui-disabled': {
            backgroundColor: statusBackgroundColor,
            borderColor: statusColor,
            cursor: 'not-allowed',
            opacity: 0.6,
            pointerEvents: 'auto',
            '& .MuiSelect-select.Mui-disabled': {
                WebkitTextFillColor: statusColor,
                cursor: 'not-allowed',
            },
            '& .MuiSelect-icon.Mui-disabled': {
                color: statusColor,
            },
        },
    } : {
        '&.Mui-disabled': {
            cursor: 'not-allowed',
            pointerEvents: 'auto',
        },
    }

    const mergedSx = sx
        ? (Array.isArray(sx) ? [statusSx, ...sx] : [statusSx, sx])
        : statusSx

    return (
        <Select
            id={id}
            value={value || ''}
            onChange={event => onChange(event.target.value as ReservationStatus)}
            size={'small'}
            fullWidth={fullWidth}
            disabled={disabled}
            open={open}
            onOpen={onOpen}
            onClose={onClose}
            MenuProps={menuProps}
            inputProps={{'aria-label': ariaLabel || t('Status')}}
            sx={mergedSx}
        >
            {getReservationStatusOptions(isExistingReservation, value ? [value] : []).map(status => (
                <MenuItem key={status} value={status}>{t(`Reservation status ${status}`)}</MenuItem>
            ))}
        </Select>
    )
}

export default ReservationStatusSelect
